import { useCallback, useEffect, useRef } from 'react';

export const COLUMN_REFETCH_DEBOUNCE_MS = 400;

/**
 * Debounced refetch scheduler for column-selection changes on the list
 * pages. Ticking several columns in quick succession schedules a single
 * trailing refetch instead of one per tick; `cancel` lets the
 * options-keyed refetch effect drop a pending column refetch when a full
 * refetch is about to run anyway (it would only duplicate). The pending
 * timer is cleared on unmount.
 */
export const useColumnRefetchScheduler = (
  onRefetch: () => void
): { schedule: () => void; cancel: () => void } => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);
  const schedule = useCallback(() => {
    cancel();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onRefetch();
    }, COLUMN_REFETCH_DEBOUNCE_MS);
  }, [cancel, onRefetch]);
  useEffect(() => cancel, [cancel]);
  return { schedule, cancel };
};
